class MainProductSingleSubscription extends HTMLElement {
  constructor() {
    super();
    this.cart = document.querySelector("cart-drawer");
    this.productOptions = this.querySelector("product-options");
    this.flushDialog = document.querySelector("#how-many-flush-dialog");
    this.productData = JSON.parse(
      document.getElementById("main-product-single-subscription-data")
        .textContent
    );
  }

  /**
   * Lifecycle method called when the custom element is added to the DOM.
   * Sets up event listeners for subscription selectors and the "Add to Cart" button.
   */
  connectedCallback() {
    this.subscriptionSelectors = this.querySelector(
      "#main-product-single-subscription-container"
    );
    this.addToCartBtn = this.querySelector("#single-product-subscription-atc");
    this.savingsTag = this.querySelector("#savings-tag");
    this.openHowManyFlushDialogBtn = this.querySelector("#how-many-flush");

    this.subscriptionSelectors.addEventListener(
      "click",
      this.summaryEventHandler.bind(this)
    );

    this.addToCartBtn.addEventListener("click", this.addToCart.bind(this));

    this.productOptions?.addEventListener(
      "click",
      this.productOptionChangedEventHandler.bind(this)
    );

    this.openHowManyFlushDialogBtn?.addEventListener("click", () => {
      this.flushDialog.show();
    });
    // Initial load of the variant
    const selectedOptions = this.getSelectedOptions();
    this.loadVariant(selectedOptions);
  }

  /**
   * Lifecycle method called when the custom element is removed from the DOM.
   * Cleans up event listeners to avoid memory leaks.
   */
  disconnectedCallback() {
    this.subscriptionSelectors.removeEventListener("click");
    this.addToCartBtn.removeEventListener("click");
  }

  /**
   * Event handler for subscription option toggling.
   * Updates the open state of subscription details and marks the selected option.
   * @param {Event} event - The click event triggered on the subscription selectors.
   */
  summaryEventHandler(event) {
    event.preventDefault();
    const currentSummary = event.target.closest("summary");
    if (!currentSummary) return;

    // Close all details and reset their radio button states
    this.subscriptionSelectors.querySelectorAll("details").forEach((el) => {
      el.removeAttribute("open");
      el.querySelector(".subscription-radio-button").setAttribute(
        "data-subscription-radio-button-checked",
        false
      );
    });

    // Mark the selected subscription option
    currentSummary
      .querySelector(".subscription-radio-button")
      .setAttribute("data-subscription-radio-button-checked", true);

      // Open the relevant details
      currentSummary.parentElement.open = true;

    //  update savings display on switch <----
    const selectedOptions = this.getSelectedOptions();
    const [variant] = this.productData.variants.filter(
      (el) => el.handle === selectedOptions
    );
    this.updateSavingsTag(variant, this.currentPurchaseType);
  }

  /**
   * Event handler for product options
   */
  productOptionChangedEventHandler(e) {
    let btn = e.target.closest("button");
    if (!btn.getAttribute("data-option-handle")) return;
    const selectedOptions = this.getSelectedOptions();
    this.loadVariant(selectedOptions);
  }

  /**
   * Search for the variant object based on the handle
   * Then updates the content
   * @param {String} variantHandle Selected variant handle
   */
  loadVariant(variantHandle) {
    const [variant] = this.productData.variants.filter(
      (el) => el.handle === variantHandle
    );
    this.updatePrices(variant);
    this.updateSummariesId(variant);
    this.updateSavingsTag(variant, this.currentPurchaseType);
  }

  /**
   * Updates the prices based on the selected variant
   * @param {Object} variant
   */
  updatePrices(variant) {
    // Update the prices in the subscription selectors
    const { price, sellingPlanPrice, comparePrice } = variant;
    this.subscriptionSelectors.querySelector(
      "#current-selling-plan-price"
    ).innerHTML = `$${sellingPlanPrice} /mo`;
    this.subscriptionSelectors
      .querySelectorAll(".current-variant-price")
      .forEach(
        (el) => (el.innerHTML = comparePrice === 0 ? "" : `$${comparePrice}`)
      );
    // .forEach((el) => (el.innerHTML = `$${price}`));

    // Update the prices in the one time  purchase
    this.querySelector("#one-time-compare-at-price").innerHTML = `${
      comparePrice === 0 || comparePrice === price ? "" : `$${comparePrice}`
    }`;
    this.querySelector("#one-time-variant-price").innerHTML = `$${price}`;
  }

  /**
   * Updates the summaries variant id
   * @param {Object} variant
   */
  updateSummariesId(variant) {
    this.subscriptionSelectors
      .querySelectorAll(".subscription-radio-button")
      .forEach((el) => (el.dataset.currentVariantId = variant.id));
  }

  /**
   * Updates the savings percentage based on the variant's price and purchase type
   * @param {Object} variant
   * @param {String} purchaseType - "one-time" or "subscription"
   */
  updateSavingsTag(variant, purchaseType = "subscription") {
    const { price, comparePrice, sellingPlanPrice } = variant;

    // Get the setting for whether to show savings tag on one-time purchase
    const showOnOneTime =
      this.savingsTag.getAttribute("data-show-savings-one-time-purchase") ===
      "true";

    // Determine if we should show the tag
    if (purchaseType === "one-time" && !showOnOneTime) {
      this.savingsTag.style.display = "none";
      return;
    }

    // Determine base price for savings calculation
    const basePrice = comparePrice > 0 ? comparePrice : price;

    let savings = 0;
    if (purchaseType === "subscription") {
      savings = Math.round(((basePrice - sellingPlanPrice) / basePrice) * 100);
    } else {
      // One-time: could show the absolute difference to compare_at_price
      savings =
        comparePrice > 0
          ? Math.round(((comparePrice - price) / comparePrice) * 100)
          : 0;
    }

    if (savings <= 0) {
      this.savingsTag.style.display = "none";
      return;
    }

    this.savingsTag.style.display = "block";
    if (purchaseType === "subscription") {
      this.savingsTag.innerHTML = `Save ${savings}%`;
      this.savingsTag.classList.remove("one-time-purchase-active");
    } else {
      // One-time logic: customize message if desired
      this.savingsTag.innerHTML = `Save ${savings}% vs Regular Price`;
      this.savingsTag.classList.add("one-time-purchase-active");
    }
  }

  /**
   * Builds the variant handle based on the selected options
   * @returns {String} variant handle
   */
  getSelectedOptions() {
    return Array.from(
      this.productOptions.querySelectorAll(
        `button[data-variant-selected="true"]`
      )
    )
      .map((el) => {
        return el.dataset.optionHandle;
      })
      .join("-");
  }

  /**
   * Getter for current purchase type ("subscription" or "one-time")
   */
  get currentPurchaseType() {
    const details = this.subscriptionSelectors.querySelector("details[open]");
    if (!details) return "subscription";
    // First details is "subscription", second is "one-time"
    return details === this.subscriptionSelectors.querySelectorAll("details")[1]
      ? "one-time"
      : "subscription";
  }

  /**
   * Handles adding a subscription product to the cart.
   * Sends a request to Shopify's cart API and updates the cart contents.
   */
  async addToCart() {
    const formData = this.buildFormData();
    const { variantId } = formData;

    try {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.innerHTML = "ADDING TO THE CART";

      const request = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const response = await request.json();

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "product-form",
        productVariantId: variantId,
        cartData: response,
      });

      this.cart.renderContents(response);
    } catch (error) {
      console.log(`❌ Single Subscription | addToCart | ${error}`);
      alert(error);
      if (typeof hj === "function") {
        hj('event', 'error_occurred');
      }
    } finally {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.innerHTML = "ADD TO CART";
      if (this.cart && this.cart.classList.contains("is-empty"))
        this.cart.classList.remove("is-empty");
    }
  }

  /**
   * Builds the form data required for adding a subscription product to the cart.
   * @returns {Object} The form data object containing item details and rendering information.
   */
  buildFormData() {
    const openDetails =
      this.subscriptionSelectors.querySelector("details[open]");
    const { sellingPlanId, currentVariantId } = openDetails.querySelector(
      ".subscription-radio-button"
    ).dataset;

    return {
      items: [
        {
          id: currentVariantId,
          quantity: 1,
          ...(sellingPlanId && {
            selling_plan: sellingPlanId,
          }),
        },
      ],
      sections: this.cart.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    };
  }
}

customElements.define(
  "main-product-single-subscription",
  MainProductSingleSubscription
);
